import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, X, Camera, Mic, Square, Loader2, Check, Image as ImageIcon } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/use-toast';

interface ChallengeTheAIProps {
  question?: string;
  answerContext?: unknown;
}

export function ChallengeTheAI({ question, answerContext }: ChallengeTheAIProps) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [recommendation, setRecommendation] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [recording, setRecording] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      mediaRecorderRef.current?.stream?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        setAudioBlob(new Blob(chunksRef.current, { type: 'audio/webm' }));
        stream.getTracks().forEach((t) => t.stop());
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch {
      toast({ title: 'Microphone unavailable', description: 'Please allow microphone access to record.', variant: 'destructive' });
    }
  }

  function stopRecording() {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  }

  function handlePhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    setPhotos((prev) => [...prev, ...files].slice(0, 5));
    e.target.value = '';
  }

  function reset() {
    setRecommendation('');
    setPhotos([]);
    setAudioBlob(null);
    setSubmitted(false);
  }

  async function handleSubmit() {
    if (!user) {
      toast({ title: 'Sign in required', description: 'Please sign in to submit a recommendation.', variant: 'destructive' });
      return;
    }
    if (recommendation.trim().length < 10) {
      toast({ title: 'Recommendation too short', description: 'Please share at least a sentence.', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      const photoUrls: string[] = [];
      for (const file of photos) {
        const path = `${user.id}/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_')}`;
        const { error } = await supabase.storage.from('challenge-submissions').upload(path, file);
        if (error) throw error;
        photoUrls.push(path);
      }

      let audioPath: string | null = null;
      if (audioBlob) {
        audioPath = `${user.id}/${Date.now()}-explanation.webm`;
        const { error } = await supabase.storage.from('challenge-submissions').upload(audioPath, audioBlob, { contentType: 'audio/webm' });
        if (error) throw error;
      }

      const { error: insertError } = await supabase.from('challenge_submissions' as any).insert({
        user_id: user.id,
        user_email: user.email ?? null,
        question: question ?? null,
        answer_context: (answerContext ?? null) as any,
        recommendation: recommendation.trim(),
        photo_urls: photoUrls,
        audio_url: audioPath,
      });
      if (insertError) throw insertError;

      setSubmitted(true);
      toast({ title: 'Recommendation submitted', description: 'Thank you — every submission is reviewed personally.' });
    } catch (e) {
      toast({ title: 'Submission failed', description: (e as Error).message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="relative overflow-hidden rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/10 via-background to-accent/10 p-6"
      >
        <div className="mb-3 flex items-center gap-2">
          <Trophy className="h-5 w-5 text-primary" />
          <h3 className="font-display text-lg text-foreground">Beat the AI Challenge</h3>
        </div>
        <p className="font-body text-sm font-medium text-foreground/90">
          Think you have a better solution?
        </p>
        <p className="mt-2 font-body text-sm leading-relaxed text-muted-foreground">
          Hair Pro Encyclopedia.ai is built to become the world's smartest beauty encyclopedia — and great
          professionals help make it even better. Submit your professional recommendation for this consultation.
        </p>
        <ul className="mt-3 space-y-1.5 font-body text-sm text-foreground/85">
          <li>🏆 Build your professional reputation within Hair Pro Encyclopedia.ai</li>
          <li>🌎 Help educate beauty professionals around the world</li>
        </ul>
        <p className="mt-3 font-body text-xs italic text-muted-foreground">
          Every submission is personally reviewed before becoming part of Hair Pro Encyclopedia.ai.
        </p>
        <motion.button
          whileHover={{ y: -1 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setOpen(true)}
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3 font-body text-sm font-semibold text-primary-foreground shadow-md transition-all"
        >
          <Trophy className="h-4 w-4" />
          Beat the AI Challenge
        </motion.button>
      </motion.div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 backdrop-blur-sm p-0 sm:items-center sm:p-4"
            onClick={() => !submitting && setOpen(false)}
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg rounded-t-3xl sm:rounded-3xl bg-card border border-border shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
            >
              <div className="flex items-center justify-between border-b border-border px-5 py-4">
                <div className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-primary" />
                  <h3 className="font-display text-lg text-foreground">Your Professional Recommendation</h3>
                </div>
                <button
                  onClick={() => !submitting && setOpen(false)}
                  className="rounded-full p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {submitted ? (
                <div className="flex flex-col items-center justify-center px-6 py-10 text-center">
                  <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/15">
                    <Check className="h-7 w-7 text-primary" />
                  </div>
                  <h4 className="font-display text-xl text-foreground">Submitted for review</h4>
                  <p className="mt-2 max-w-sm font-body text-sm text-muted-foreground">
                    Thank you for contributing. Your recommendation will be personally reviewed before being added to
                    Hair Pro Encyclopedia.ai.
                  </p>
                  <button
                    onClick={() => { reset(); setOpen(false); }}
                    className="mt-6 rounded-xl bg-primary px-6 py-2.5 font-body text-sm font-semibold text-primary-foreground"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
                  <div>
                    <label className="mb-2 flex items-center gap-2 font-body text-sm font-medium text-foreground">
                      💬 Describe your solution
                    </label>
                    <textarea
                      value={recommendation}
                      onChange={(e) => setRecommendation(e.target.value.slice(0, 4000))}
                      rows={6}
                      placeholder="Share your professional approach, formula, or correction path…"
                      className="w-full resize-none rounded-xl border border-border bg-white px-4 py-3 font-body text-base text-black font-bold placeholder:text-gray-500 focus:border-primary focus:outline-none"
                    />
                    <p className="mt-1 text-right text-xs text-muted-foreground">{recommendation.length}/4000</p>
                  </div>

                  <div>
                    <label className="mb-2 flex items-center gap-2 font-body text-sm font-medium text-foreground">
                      📷 Upload photos <span className="text-xs font-normal text-muted-foreground">(optional, up to 5)</span>
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoChange}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-muted/30 px-4 py-3 font-body text-sm text-muted-foreground hover:border-primary/40 hover:text-foreground"
                    >
                      <Camera className="h-4 w-4" />
                      {photos.length ? `${photos.length} photo${photos.length > 1 ? 's' : ''} selected` : 'Choose photos'}
                    </button>
                    {photos.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {photos.map((f, i) => (
                          <div key={i} className="flex items-center gap-1 rounded-lg bg-muted px-2 py-1 text-xs text-foreground">
                            <ImageIcon className="h-3 w-3" />
                            <span className="max-w-[120px] truncate">{f.name}</span>
                            <button
                              onClick={() => setPhotos((prev) => prev.filter((_, idx) => idx !== i))}
                              className="ml-1 text-muted-foreground hover:text-destructive"
                              aria-label="Remove"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="mb-2 flex items-center gap-2 font-body text-sm font-medium text-foreground">
                      🎤 Record your explanation <span className="text-xs font-normal text-muted-foreground">(optional)</span>
                    </label>
                    {audioBlob ? (
                      <div className="flex items-center gap-2 rounded-xl border border-border bg-muted/30 px-3 py-2">
                        <audio controls src={URL.createObjectURL(audioBlob)} className="h-9 w-full" />
                        <button
                          onClick={() => setAudioBlob(null)}
                          className="rounded-full p-1 text-muted-foreground hover:text-destructive"
                          aria-label="Remove recording"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={recording ? stopRecording : startRecording}
                        className={`flex w-full items-center justify-center gap-2 rounded-xl border px-4 py-3 font-body text-sm transition-colors ${
                          recording
                            ? 'border-destructive/40 bg-destructive/10 text-destructive'
                            : 'border-dashed border-border bg-muted/30 text-muted-foreground hover:border-primary/40 hover:text-foreground'
                        }`}
                      >
                        {recording ? <Square className="h-4 w-4 animate-pulse" /> : <Mic className="h-4 w-4" />}
                        {recording ? 'Stop recording' : 'Start recording'}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {!submitted && (
                <div className="border-t border-border bg-background/50 px-5 py-4">
                  <button
                    onClick={handleSubmit}
                    disabled={submitting || recommendation.trim().length < 10}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3 font-body text-sm font-semibold text-primary-foreground shadow-md transition-all disabled:opacity-50"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trophy className="h-4 w-4" />}
                    {submitting ? 'Submitting…' : 'Submit Recommendation'}
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
